import { cn } from "@veo/ui";

/**
 * Loading and empty states.
 *
 * Skeletons mirror the shape of the content they replace rather than being a
 * generic spinner, so the layout does not lurch when real content arrives.
 * Every one carries `aria-hidden` with a live-region announcement alongside:
 * a screen reader should hear "loading", not read out a dozen grey boxes.
 */

export function Shimmer({ className }: { className?: string }) {
  return <div aria-hidden className={cn("animate-pulse rounded bg-ink-200", className)} />;
}

export function SectionSkeleton() {
  return (
    <section className="py-16 md:py-24" aria-busy="true">
      <span className="sr-only" role="status">
        Loading section
      </span>
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8">
        <Shimmer className="h-3 w-24" />
        <Shimmer className="mt-4 h-9 w-2/3" />
        <Shimmer className="mt-6 h-4 w-full" />
        <Shimmer className="mt-2 h-4 w-5/6" />
      </div>
    </section>
  );
}

export function ArticleCardSkeleton() {
  return (
    <div aria-hidden className="overflow-hidden rounded-lg bg-white shadow-card">
      <Shimmer className="aspect-[16/10] w-full rounded-none" />
      <div className="space-y-3 p-6">
        <Shimmer className="h-3 w-20" />
        <Shimmer className="h-6 w-full" />
        <Shimmer className="h-4 w-4/5" />
      </div>
    </div>
  );
}

export function ArticleGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div aria-busy="true">
      <span className="sr-only" role="status">
        Loading articles
      </span>
      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: count }, (_, index) => (
          <ArticleCardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-ink-300 px-6 py-16 text-center">
      <p className="font-display text-xl text-navy-900">{title}</p>
      {description && <p className="mx-auto mt-2 max-w-md text-ink-600">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description = "We could not load this content just now. Please try again in a moment.",
  action,
}: {
  title?: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-danger-500/20 bg-danger-50 px-6 py-12 text-center"
    >
      <p className="font-display text-xl text-danger-700">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-danger-700/80">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
